import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import { FC } from "react";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../../../../components";
import MuiEllipseLabel from "../../../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../../../../../helpers/types/common";
import { buildActionMenuItems } from "../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../../../hooks/useSiteCore";
import {
  ProjectDeliverablesFormKeys,
  ProjectDeliverablesTabProps,
} from "../modelsAndSchemas/models";
import { deliverablesListSiteCoreKeys } from "../siteCore/SiteCoreKeys";
export const pageId = "Create-Client-Activities-tab";

export const ProjectDeliverablesTab: FC<ProjectDeliverablesTabProps> = ({
  rows,
  control,
  tableRef,
  isView,
  uniqueFilters,
  filteredResults,
  onProjectDeliverableViewClick,
  deleteAction,
}) => {
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const onActionClick = (action: TableActionType, row: any) => {
    if (action === TableActions.edit) {
      onProjectDeliverableViewClick(row, true);
    } else {
      deleteAction(row);
    }
  };
  const renderDeliverableFormControl = (
    control: any,
    name: keyof typeof ProjectDeliverablesFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ProjectDeliverablesFormKeys.name}
      key={ProjectDeliverablesFormKeys.name}
    >
      <MuiTextField
        id={`${pageId}-${ProjectDeliverablesFormKeys.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderDeliverableFormControl(
      control,
      ProjectDeliverablesFormKeys.deliverableType,
      uniqueFilters.deliverableType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectDeliverablesFormKeys.deliverableType}`,
    ),

    renderDeliverableFormControl(
      control,
      ProjectDeliverablesFormKeys.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectDeliverablesFormKeys.units}`,
    ),
    <></>,
    renderDeliverableFormControl(
      control,
      ProjectDeliverablesFormKeys.activityType,
      uniqueFilters.activityType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectDeliverablesFormKeys.activityType}`,
    ),
    <></>,
  ];

  const columns = [
    {
      id: ProjectDeliverablesFormKeys.name,
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.deliverableName,
      ),
      render: (row: any) =>
        !row.name ? null : (
          <MuiLink onClick={() => onProjectDeliverableViewClick(row)}>
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link ellipsis-with-tooltip"
                text={row.name}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
      className: "cell-deliverable-name",
    },
    {
      id: ProjectDeliverablesFormKeys.deliverableType,
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.deliverableType,
      ),
      isSortVisible: true,
      className: "cell-deliverable-type",
    },
    {
      id: ProjectDeliverablesFormKeys.units,
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.units,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: ProjectDeliverablesFormKeys.measurements,
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.measurement,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },
    {
      id: ProjectDeliverablesFormKeys.activityType,
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.activityType,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },
    {
      id: "action",
      label: fetchLabel(
        deliverablesListSiteCoreKeys.deliverablesList,
        deliverablesListSiteCoreKeys.action,
      ),
      render: (row: any) => {
        const actions = [
          {
            permissionKey:
              MODULE_PERMISSIONS.PROJECT_EDIT_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              label: commonLabels.edit,
              dataTestId: "edit",
              onClick: () => onActionClick(TableActions.edit, row),
            }),
          },
        ];
        if (!row?.isProjectFrameworkDeliverable) {
          actions.push({
            permissionKey:
              MODULE_PERMISSIONS.PROJECT_DELETE_ACTIVITIES_AND_DELIVERABLES,

            create: () => ({
              label: commonLabels.delete,
              dataTestId: "delete",
              onClick: () => onActionClick(TableActions.delete, row),
            }),
          });
        }

        const menuItems = buildActionMenuItems(
          row,
          actions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={
            isView ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          columns={isView ? columns.slice(0, -1) : columns}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            deliverablesListSiteCoreKeys.deliverablesList,
            deliverablesListSiteCoreKeys.deliverablesNotAvailable,
          )}
          priorityResults={[]}
          isLastColumnSticky={!isView}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
